// ---------------------------------------------------------------------------
// TemplatePreview — Read-only template tree for review page
// ---------------------------------------------------------------------------
// Mirrors the PromptTree nesting logic: root prompts at top level, branch
// targets indented under the answer that routes to them with a pill showing
// which answer triggered the branch.

import { useMemo } from 'react';
import { Info, ArrowDown } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import type {
  TemplateSession,
  TemplateDataGroup,
  TemplatePrompt,
  AlertLevel,
} from '@/types/template';

const ALERT_COLORS: Record<AlertLevel, string> = {
  0: 'bg-green-100 text-green-700 border-green-200',
  1: 'bg-green-50 text-green-600 border-green-100',
  2: 'bg-honey-100 text-honey-800 border-honey-200',
  3: 'bg-red-100 text-red-700 border-red-200',
  4: 'bg-red-200 text-red-800 border-red-300',
};

const PILL_CLASS = 'bg-purple-100 text-purple-700 border border-purple-300';

interface TreeNode {
  prompt: TemplatePrompt;
  depth: number;
  parentAnswerText?: string;
  children: TreeNode[];
}

interface TemplatePreviewProps {
  sessions: TemplateSession[];
  dataGroups?: TemplateDataGroup[];
}

function buildTree(
  prompts: TemplatePrompt[],
): TreeNode[] {
  const promptById = new Map<string, TemplatePrompt>();
  for (const p of prompts) promptById.set(p.prompt_id, p);

  const branchTargetIds = new Set<string>();
  for (const p of prompts) {
    for (const a of p.answers) {
      if (a.next_prompt_id) branchTargetIds.add(a.next_prompt_id);
    }
  }

  const roots = prompts
    .filter((p) => !branchTargetIds.has(p.prompt_id))
    .sort((a, b) => a.sort_order - b.sort_order);

  function buildNode(
    prompt: TemplatePrompt,
    depth: number,
    visited: Set<string>,
    parentAnswerText?: string,
  ): TreeNode | null {
    if (visited.has(prompt.prompt_id)) return null;
    visited.add(prompt.prompt_id);

    const children: TreeNode[] = [];
    const sortedAnswers = [...prompt.answers].sort(
      (a, b) => a.sort_order - b.sort_order,
    );

    const routedIds = sortedAnswers
      .filter((a) => a.next_prompt_id)
      .map((a) => a.next_prompt_id!);
    const allSameTarget =
      prompt.answers.length > 1 &&
      routedIds.length === prompt.answers.length &&
      routedIds.every((id) => id === routedIds[0]);

    const seen = new Set<string>();
    for (const answer of sortedAnswers) {
      if (answer.next_prompt_id && !seen.has(answer.next_prompt_id)) {
        seen.add(answer.next_prompt_id);
        const child = promptById.get(answer.next_prompt_id);
        if (child) {
          const pillText = allSameTarget ? 'Always' : answer.answer_text;
          const childNode = buildNode(child, depth + 1, visited, pillText);
          if (childNode) children.push(childNode);
        }
      }
    }

    return { prompt, depth, parentAnswerText, children };
  }

  const visited = new Set<string>();
  const nodes: TreeNode[] = [];
  for (const root of roots) {
    const node = buildNode(root, 0, visited);
    if (node) nodes.push(node);
  }
  return nodes;
}

function PreviewNode({ node }: { node: TreeNode }) {
  const { prompt, parentAnswerText, children } = node;

  return (
    <div>
      {parentAnswerText && (
        <div className="flex items-center gap-1 mb-1 ml-2">
          <Badge className={PILL_CLASS}>{parentAnswerText}</Badge>
          <ArrowDown className="h-3 w-3 text-purple-400" />
        </div>
      )}

      <Card className={parentAnswerText ? 'border-l-2 border-l-purple-200' : ''}>
        <CardContent className="p-3">
          <div className="flex items-center gap-2 mb-1">
            {prompt.is_informational ? (
              <Badge variant="secondary" className="gap-1">
                <Info className="h-3 w-3" /> Info
              </Badge>
            ) : (
              <Badge variant="default">Q-{prompt.question_id}</Badge>
            )}
            <span className="text-xs text-carbon-400">#{prompt.sort_order}</span>
          </div>

          <p className="text-sm text-carbon-800 leading-relaxed">
            {prompt.is_informational
              ? prompt.prompt_text
              : prompt.prompt_text ?? `(Question #${prompt.question_id})`}
          </p>

          {prompt.answers.length > 0 && (
            <div className="mt-2 space-y-1">
              {[...prompt.answers]
                .sort((a, b) => a.sort_order - b.sort_order)
                .map((answer) => (
                  <div
                    key={answer.prompt_answer_id}
                    className={`flex items-center gap-2 rounded-ds-004 border px-3 py-1 text-xs ${ALERT_COLORS[answer.alert_level]}`}
                  >
                    <span className="font-medium shrink-0">L{answer.alert_level}</span>
                    <span className="flex-1">{answer.answer_text}</span>
                    {answer.next_prompt_id && (
                      <span className="text-[10px] opacity-70">
                        &rarr; #{children.find(
                          (c) => c.prompt.prompt_id === answer.next_prompt_id
                        )?.prompt.sort_order ?? answer.next_prompt_id.slice(0, 8)}
                      </span>
                    )}
                  </div>
                ))}
            </div>
          )}
        </CardContent>
      </Card>

      {children.length > 0 && (
        <div className="ml-8 mt-1 space-y-2">
          {children.map((child) => (
            <PreviewNode key={child.prompt.prompt_id} node={child} />
          ))}
        </div>
      )}
    </div>
  );
}

export function TemplatePreview({ sessions, dataGroups }: TemplatePreviewProps) {
  const treeBySession = useMemo(() => {
    const map = new Map<number, TreeNode[]>();
    for (const session of sessions) {
      map.set(session.sessionNumber, buildTree(session.prompts));
    }
    return map;
  }, [sessions]);

  return (
    <div className="space-y-4">
      {dataGroups && dataGroups.length > 0 && (
        <div className="flex flex-wrap gap-2 p-3 rounded-ds-008 bg-silver-50 border border-silver-200">
          <span className="text-xs font-semibold text-carbon-600 mr-1">Data Groups:</span>
          {dataGroups.map((g) => (
            <Badge key={g.template_data_group_id} variant="outline">
              {g.code && <>{g.code} &mdash; </>}
              {g.name ?? `Data group ${g.data_group_id}`}
            </Badge>
          ))}
        </div>
      )}

      {[...sessions]
        .sort((a, b) => a.sessionNumber - b.sessionNumber)
        .map((session) => {
          const nodes = treeBySession.get(session.sessionNumber) ?? [];
          return (
            <div key={session.sessionNumber}>
              <h4 className="text-sm font-semibold text-carbon-700 mb-2">
                Session {session.sessionNumber}
                <span className="text-xs font-normal text-carbon-400 ml-2">
                  {session.prompts.length} prompt{session.prompts.length !== 1 ? 's' : ''}
                </span>
              </h4>

              <div className="space-y-2 ml-2">
                {nodes.map((node) => (
                  <PreviewNode key={node.prompt.prompt_id} node={node} />
                ))}
              </div>
            </div>
          );
        })}

      {sessions.length === 0 && (
        <p className="text-sm text-carbon-500 text-center py-6">No prompts in this template.</p>
      )}
    </div>
  );
}
